/**
 * Token management service for handling authentication tokens.
 * Extracted from AuthContext to improve testability and separation of concerns.
 */

import type { User } from '../contexts/AuthContext'
import type { StorageAdapter } from '../lib/storage'
import { browserStorage, STORAGE_KEYS } from '../lib/storage'
import { systemClock, type Clock } from '../lib/clock'

/**
 * Interface for token management operations.
 */
export interface TokenManager {
    /**
     * Get the current access token.
     */
    getAccessToken(): string | null

    /**
     * Get the current refresh token.
     */
    getRefreshToken(): string | null

    /**
     * Get the stored user data.
     */
    getUserFromStorage(): User | null

    /**
     * Store the access token.
     */
    setAccessToken(token: string): void

    /**
     * Store the refresh token.
     */
    setRefreshToken(token: string): void

    /**
     * Store user data.
     */
    setUserInStorage(user: User): void

    /**
     * Clear all authentication tokens and user data.
     */
    clearTokens(): void

    /**
     * Check if a JWT token is expired.
     * @param token - The JWT token to check
     * @returns true if the token is expired or invalid, false otherwise
     */
    isTokenExpired(token: string): boolean

    /**
     * Get the expiration time of a JWT token.
     * @param token - The JWT token
     * @returns The expiration timestamp in milliseconds, or null if invalid
     */
    getTokenExpiration(token: string): number | null
}

/**
 * Factory function to create a TokenManager instance.
 * Use this in tests with a memory storage adapter and/or test clock.
 *
 * @param storage - The storage adapter to use (defaults to browserStorage)
 * @param clock - The clock to use for time operations (defaults to systemClock)
 * @returns A TokenManager instance
 */
export const createTokenManager = (
    storage: StorageAdapter = browserStorage,
    clock: Clock = systemClock
): TokenManager => {
    const getAccessToken = (): string | null => {
        return storage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
    }

    const getRefreshToken = (): string | null => {
        return storage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
    }

    const getUserFromStorage = (): User | null => {
        const userJson = storage.getItem(STORAGE_KEYS.USER)
        if (!userJson) return null

        try {
            return JSON.parse(userJson) as User
        } catch {
            return null
        }
    }

    const setAccessToken = (token: string): void => {
        storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token)
    }

    const setRefreshToken = (token: string): void => {
        storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token)
    }

    const setUserInStorage = (user: User): void => {
        storage.setItem(STORAGE_KEYS.USER, JSON.stringify(user))
    }

    const clearTokens = (): void => {
        storage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
        storage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
        storage.removeItem(STORAGE_KEYS.USER)
    }

    const parseJwtPayload = (token: string): { exp?: number } | null => {
        try {
            const parts = token.split('.')
            if (parts.length !== 3) return null
            const payload = atob(parts[1])
            return JSON.parse(payload) as { exp?: number }
        } catch {
            return null
        }
    }

    const isTokenExpired = (token: string): boolean => {
        const payload = parseJwtPayload(token)
        if (!payload || payload.exp === undefined) {
            return true // Treat invalid tokens as expired
        }
        return payload.exp * 1000 < clock.now()
    }

    const getTokenExpiration = (token: string): number | null => {
        const payload = parseJwtPayload(token)
        if (!payload || payload.exp === undefined) {
            return null
        }
        return payload.exp * 1000
    }

    return {
        getAccessToken,
        getRefreshToken,
        getUserFromStorage,
        setAccessToken,
        setRefreshToken,
        setUserInStorage,
        clearTokens,
        isTokenExpired,
        getTokenExpiration,
    }
}

/**
 * Default singleton instance for production use.
 */
export const tokenManager = createTokenManager()
